<template>
  <Card :loadingState="loading" :title="cardTitle">
    <template v-slot:content>
      <Collapser
        v-if="showCollapser"
        :collapserTitle="collapserTitle"
        :expanded="isExpanded"
        trackId="togglePersonalData"
      >
        <template v-slot:collapserContent>
          <PartnerAddressContent
            v-if="!loading"
            :dataLineWhitelist="dataLineWhitelist"
            :hasError="hasError"
            :partnerAddress="partnerAddress"
            :showSelfServiceIcon="showSelfServiceIcon"
          ></PartnerAddressContent>
        </template>
      </Collapser>
      <div v-else>
        <PartnerAddressContent
          v-if="!loading"
          :dataLineWhitelist="dataLineWhitelist"
          :hasError="hasError"
          :partnerAddress="partnerAddress"
          :showSelfServiceIcon="showSelfServiceIcon"
        ></PartnerAddressContent>
      </div>
    </template>
  </Card>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import { Card, Collapser } from 'de-zurich-ui-library'
import { type PartnerAddress, ResponseError } from 'partner-api'
import { partnerApi } from '@/stores/partnerApiClient'
import PartnerAddressContent from './PartnerAddressContent.vue'

import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
  showSelfServiceIcon: {
    type: String,
    default: 'true'
  },
  policyId: {
    type: String
  },
  partnerAddressId: {
    type: String
  },
  useCollapser: {
    type: String,
    default: 'false'
  },
  expanded: {
    type: String,
    default: 'false'
  },
  title: {
    type: String
  },
  linesWhitelist: {
    type: String
  }
})

const loading = ref(true)
const hasError = ref(false)
const partnerAddress = ref<PartnerAddress | null>(null)

onMounted(async () => {
  try {
    let response
    if (props.partnerAddressId) {
      response = await partnerApi().getPartnerAddressById({
        partnerAddressId: props.partnerAddressId
      })
    } else if (props.policyId) {
      response = await partnerApi().getPartnerAddressByPartnerProxyId({
        policyId: props.policyId
      })
    } else {
      throw new Error(
        'One of partner-address-id or policy-id attribute MUST be provided!'
      )
    }

    partnerAddress.value = response.data ?? null
    hasError.value = false
  } catch (err) {
    partnerAddress.value = null
    hasError.value = true
    if (!(err instanceof ResponseError)) {
      console.error('Error:', err)
    }
  } finally {
    loading.value = false
  }
})

const showCollapser = props.useCollapser === 'true'
const isExpanded = props.expanded === 'true'
const collapserTitle = props.title ?? String(t('partnerAddress.title'))
const cardTitle = showCollapser ? '' : collapserTitle
const dataLineWhitelist = computed(() => {
  return props.linesWhitelist
    ? props.linesWhitelist
        .split(',')
        .map((whitelistedLine: string) => whitelistedLine.trim())
    : undefined
})
</script>
